import { useState } from "react";
import { UserOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Link } from "@tanstack/react-router";
import { Avatar, Input, Pagination } from "antd5";
import { isEqual } from "lodash";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import EmptyState from "components/ui/empty_state/EmptyState";
import { appRoutes } from "lib/appRoutes";
import { Table } from "lib/core_components/Table";
import { createUseDebounce } from "lib/debounce";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useCreatePinnedView } from "lib/hooks/api/pinned_views/useCreatePinnedView";
import { useDeletePinnedView } from "lib/hooks/api/pinned_views/useDeletePinnedView";
import { useSavedViews } from "lib/hooks/api/saved_views/useSavedViews";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { useURLState } from "lib/hooks/useURLState";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { toAndSearch } from "lib/spaNavigation";
import { SavedViewSortOrder } from "lib/types/graphQLEnums";
import { SavedViewFilters, savedViewFilters } from "lib/types/savedViews";
import { formatDate } from "lib/utils";
import { commonTableColumns } from "../../lib/core_components/Table/ColumnTypes";
import UserInitials from "../comments/UserInitials";
import { UserDropdownFilter } from "../form_components/dropdown_list_selects/UserDropdownFilter";
import UserName from "../users/UserName";
import { DataTypeDropdownFilter } from "./DataTypeDropdownFilter";
import { DeleteViewModal } from "./DeleteViewModal";
import { DuplicateViewModal } from "./DuplicateViewModal";
import { EditAlertsModal } from "./EditAlertsModal";
import { EditViewModal } from "./EditViewModal";
import { generateSavedViewUrl, viewActionTooltip } from "./utils";

import css from "./FilterableSavedViews.module.scss";

type FilterableSavedViewsProps = {
  defaultFilters: SavedViewFilters;
};

const getSortOrder = (filters: SavedViewFilters, key: `${SavedViewFilters["orderBy"]}`) => {
  if (filters.orderBy === key) {
    return filters.order === SavedViewSortOrder.Asc ? "ascend" : "descend";
  }
  return null;
};

export function FilterableSavedViews({ defaultFilters }: FilterableSavedViewsProps) {
  const isRelevancySearchNewUsersEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const [filters = defaultFilters, setFilters] = useURLState(
    "savedSearchFilters",
    defaultFilters,
    savedViewFilters,
    { encoder: "compress" },
  );

  const [pageSize, setPageSize] = useTablePageSize("saved-searches-table");
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState(filters.text);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isEditAlertsModalOpen, setIsEditAlertsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [selectedViewForActions, setSelectedViewForActions] = useState<{
    id: string;
    name: string;
    description?: string | null;
    isStandardView?: boolean;
  } | null>(null);

  const useDebounce = createUseDebounce(500);
  const debouncedSetFilters = useDebounce((f: SavedViewFilters) => {
    setFilters(f);
    setPage(1);
  });

  const { data, isLoading, isError } = useSavedViews(filters, {
    page,
    pageSize,
  });
  const { mutate: pinView } = useCreatePinnedView();
  const { mutate: unpinView } = useDeletePinnedView();

  const user = useAssertCurrentUser();
  const getDataTypeLabel = (dataType: string) => {
    if (dataType === "noticeSemantic") {
      return isRelevancySearchNewUsersEnabled ? "Notices" : "Notices v2";
    }
    return `${dataType}s`;
  };

  return (
    <Flex column gap="24px" width="100%">
      <FiltersContainer gap="12px" width="100%">
        <Input
          placeholder="Search name"
          prefix={<UIcon icon="searchSm" color="sysPrimaryDefault" />}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            debouncedSetFilters({ ...filters, text: e.target.value });
          }}
          allowClear
          className="searchName"
        />
        <Flex gap={8} alignItems="center">
          <Text color="sysTextSecondary">Filter by</Text>
          <DataTypeDropdownFilter
            value={filters.dataTypes ?? []}
            onChange={(value) => debouncedSetFilters({ ...filters, dataTypes: value })}
          />
          <UserDropdownFilter
            value={{ ids: filters.ownerIds ?? [], includeNulls: false }}
            onChange={(value) => debouncedSetFilters({ ...filters, ownerIds: value.ids })}
            options={{ noun: "owner", hideUnassignedOption: true }}
          />
        </Flex>
      </FiltersContainer>
      <Table
        dataSource={data?.results ?? []}
        rowKey="id"
        loading={isLoading}
        isError={isError}
        columns={[
          {
            ...commonTableColumns.titleColumn,
            key: "name",
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortOrder: getSortOrder(filters, "name"),
            render: (_, s) => (
              <Flex gap="8px" alignItems="center">
                <Flex column flexGrow={1}>
                  <Link {...toAndSearch(generateSavedViewUrl(s.id, s.dataType))}>{s.name}</Link>
                  <Text description color="sysTextSecondary">
                    {s.description}
                  </Text>
                </Flex>
                <Flex gap="4px">
                  <DropdownMenu
                    trigger={
                      <DropdownMenuTrigger
                        buttonType="tertiary"
                        hideArrow
                        icon="dotsHorizontal"
                        iconProps={{ color: "sysTextDefault" }}
                      />
                    }
                    width="fit-content"
                    modal={false}
                  >
                    <DropdownMenuItem
                      key="set-alerts"
                      icon="bell01"
                      onClick={() => {
                        setSelectedViewForActions({
                          id: s.id,
                          name: s.name,
                          description: s.description,
                          isStandardView: s.isStandardView,
                        });
                        setIsEditAlertsModalOpen(true);
                      }}
                    >
                      Set alerts
                    </DropdownMenuItem>
                    {s.pinnedRank ? (
                      <>
                        <DropdownMenuItem
                          key="unpin"
                          icon="pin02"
                          onClick={() => {
                            unpinView({ input: { savedViewId: s.id } });
                          }}
                        >
                          Unpin search
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem
                        key="pin"
                        icon="pin02"
                        onClick={() => {
                          pinView({ input: { savedViewId: s.id } });
                        }}
                      >
                        Pin search
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      key="duplicate"
                      icon="copy07"
                      onClick={() => {
                        setSelectedViewForActions({
                          id: s.id,
                          name: s.name,
                          description: s.description,
                        });
                        setIsDuplicateModalOpen(true);
                      }}
                    >
                      Duplicate search
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      key="edit"
                      icon="edit05"
                      onClick={() => {
                        setSelectedViewForActions({
                          id: s.id,
                          name: s.name,
                          description: s.description,
                          isStandardView: s.isStandardView,
                        });
                        setIsEditModalOpen(true);
                      }}
                      disabled={s.isStandardView || s.owner?.guid !== user?.guid}
                      tooltip={viewActionTooltip("edit", s, user.guid)}
                    >
                      Edit search
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      key="delete"
                      icon="trash01"
                      onClick={() => {
                        setSelectedViewForActions({
                          id: s.id,
                          name: s.name,
                          description: s.description,
                          isStandardView: s.isStandardView,
                        });
                        setIsDeleteModalOpen(true);
                      }}
                      disabled={s.isStandardView || s.owner?.guid !== user?.guid}
                      tooltip={viewActionTooltip("delete", s, user.guid)}
                    >
                      Delete search
                    </DropdownMenuItem>
                  </DropdownMenu>
                  {s.pinnedRank ? (
                    <Button
                      buttonType="tertiary"
                      onClick={() => {
                        unpinView({ input: { savedViewId: s.id } });
                      }}
                      icon="pin02Filled"
                    />
                  ) : (
                    <Button
                      buttonType="tertiary"
                      onClick={() => {
                        pinView({ input: { savedViewId: s.id } });
                      }}
                      icon="pin02"
                    />
                  )}
                </Flex>
              </Flex>
            ),
          },
          {
            key: "dataType",
            dataIndex: "dataType",
            title: "Type",
            render: (_, s) => (
              <Text textTransform="capitalize">{getDataTypeLabel(s.dataType)}</Text>
            ),
          },
          {
            key: "owner",
            dataIndex: "owner",
            title: "Owned by",
            render: (_, s) =>
              s.isStandardView ? (
                <div className={css.ownerCell}>
                  <Avatar icon={<UserOutlined />} />
                  Admin created search
                </div>
              ) : (
                <div className={css.ownerCell}>
                  <UserInitials firstName={s.owner?.firstName} lastName={s.owner?.lastName} />
                  <UserName user={s.owner} />
                </div>
              ),
          },
          {
            key: "updatedAt",
            dataIndex: "updatedAt",
            title: "Last updated",
            sorter: true,
            sortOrder: getSortOrder(filters, "updatedAt"),
            render: (_, s) => formatDate(s.updatedAt ? new Date(s.updatedAt) : undefined),
          },
        ]}
        onChange={(_, __, sorter) => {
          const currentSortState: Pick<SavedViewFilters, "order" | "orderBy"> = {
            order: filters.order,
            orderBy: filters.orderBy,
          };

          const newSort = Array.isArray(sorter) ? sorter[0] : sorter;
          if (!newSort.order) {
            if (currentSortState.orderBy !== undefined || currentSortState.order !== undefined) {
              setFilters({
                ...filters,
                order: undefined,
                orderBy: undefined,
              });
            }
            return;
          }

          const newSortState: Pick<SavedViewFilters, "order" | "orderBy"> = {
            order: newSort.order === "ascend" ? SavedViewSortOrder.Asc : SavedViewSortOrder.Desc,
            orderBy: newSort.columnKey as SavedViewFilters["orderBy"],
          };
          if (!isEqual(currentSortState, newSortState)) {
            setFilters({
              ...filters,
              ...newSortState,
            });
          }
        }}
        locale={{
          emptyText: (
            <Flex justifyContent="center" alignItems="center">
              {data.totalResults === 0 ? (
                <EmptyState
                  size="large"
                  type="userRequiredContent"
                  heading="No saved searches yet"
                  description="Your saved searches will appear here"
                  action={
                    <Button as="a" {...appRoutes.notices}>
                      Search notices
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  size="large"
                  type="noResults"
                  heading="No matches found"
                  description="Try adjusting your filters to see more results"
                />
              )}
            </Flex>
          ),
        }}
      />
      <Pagination
        current={page}
        pageSize={pageSize}
        onChange={(page, pageSize) => {
          setPageSize(pageSize);
          setPage(page);
        }}
        total={data?.totalResults || 0}
        showSizeChanger
        align="center"
      />
      {selectedViewForActions && (
        <>
          <EditAlertsModal
            isOpen={isEditAlertsModalOpen}
            selectedView={selectedViewForActions}
            onOpenChange={(open) => {
              setIsEditAlertsModalOpen(open);
              if (!open) setSelectedViewForActions(null);
            }}
          />
          <DuplicateViewModal
            isOpen={isDuplicateModalOpen}
            selectedView={selectedViewForActions}
            onOpenChange={(open) => {
              setIsDuplicateModalOpen(open);
              if (!open) setSelectedViewForActions(null);
            }}
          />
          <EditViewModal
            isOpen={isEditModalOpen}
            selectedView={selectedViewForActions}
            onOpenChange={(open) => {
              setIsEditModalOpen(open);
              if (!open) setSelectedViewForActions(null);
            }}
          />
          <DeleteViewModal
            isOpen={isDeleteModalOpen}
            selectedView={selectedViewForActions}
            onOpenChange={(open) => {
              setIsDeleteModalOpen(open);
              if (!open) setSelectedViewForActions(null);
            }}
          />
        </>
      )}
    </Flex>
  );
}

const FiltersContainer = styled(Flex)({
  alignItems: "center",
  "& .searchName": {
    maxWidth: "350px",
  },
  "@media (max-width: 960px)": {
    flexDirection: "column",
    alignItems: "flex-start",
    "& .searchName": {
      maxWidth: "100%",
    },
  },
});
